import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  ReportingExportResponse,
  ReportingGrain,
  ReportingTimezone,
} from '@eluve/api-contract';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';

type ExportQuery = {
  from: string;
  to: string;
  timezone: ReportingTimezone;
  grain: ReportingGrain;
  locationIds?: string;
  specialties?: string;
  providerIds?: string;
  templateIds?: string;
  microphones?: string;
  groupBy?: string;
  onboardedOnly: 'true' | 'false';
};

const csvEscape = (value: string | number | null): string => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

const pctOf = (numerator: number, denominator: number): number | '' =>
  denominator > 0 ? Math.round((numerator / denominator) * 100) : '';

/**
 * One row per user: group labels, whole-window rollup, then five
 * columns per bucket. Blank cells where a user had no appointments.
 */
const buildCsv = (
  data: ReportingExportResponse,
  groupLabels: string[],
): string => {
  const RATIO_HEADERS = ['Completion %', 'Note Signed %'];
  const headers = [
    ...groupLabels,
    'User',
    'Email',
    'Scheduled Appointments',
    'Completed Appointments',
    'Signed Appointments',
    ...RATIO_HEADERS,
    ...data.bucketStarts.flatMap((bucketStart) => [
      `${bucketStart} Signed`,
      `${bucketStart} Completed`,
      `${bucketStart} Scheduled`,
      ...RATIO_HEADERS.map((header) => `${bucketStart} ${header}`),
    ]),
  ];
  const lines = data.rows.map((row) => {
    const cells: Array<string | number | null> = [
      ...row.groups,
      row.providerName,
      row.providerEmail ?? '',
      row.aggregate.total,
      row.aggregate.completed,
      row.aggregate.signed,
      pctOf(row.aggregate.completed, row.aggregate.total),
      pctOf(row.aggregate.signed, row.aggregate.completed),
    ];
    for (const counts of row.buckets) {
      cells.push(
        ...(counts
          ? [
              counts.signed,
              counts.completed,
              counts.total,
              pctOf(counts.completed, counts.total),
              pctOf(counts.signed, counts.completed),
            ]
          : ['', '', '', '', '']),
      );
    }
    return cells.map(csvEscape).join(',');
  });
  return [headers.map(csvEscape).join(','), ...lines].join('\n');
};

const download = (csv: string, filename: string) => {
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

/**
 * Toolbar CSV export: opens a "Preparing your export" dialog while one
 * flat per-provider query runs, then triggers the download.
 */
export const ExportButton: React.FC<{
  tenantId: string;
  query: ExportQuery;
  /** Column headers for the groupBy dims, in the same order */
  groupLabels: string[];
  disabled?: boolean;
}> = ({ tenantId, query, groupLabels, disabled }) => {
  const apiClient = useApiClient();
  const [exportState, setExportState] = useState<
    'idle' | 'preparing' | 'error'
  >('idle');

  const handleExport = async () => {
    setExportState('preparing');
    try {
      const response = await apiClient.reporting.exportReporting({
        params: { tenantId },
        query,
      });
      if (response.status !== 200) {
        setExportState('error');
        return;
      }
      download(
        buildCsv(response.body, groupLabels),
        `eluve-report_${query.from}_${query.to}.csv`,
      );
      setExportState('idle');
    } catch {
      setExportState('error');
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        aria-label="Export CSV"
        disabled={(disabled ?? false) || exportState === 'preparing'}
        onClick={() => void handleExport()}
      >
        <Icon name="Download" size="sm" />
      </Button>
      <Dialog
        open={exportState !== 'idle'}
        onOpenChange={(open) => {
          if (!open) {
            setExportState('idle');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {exportState === 'error'
                ? 'Export failed'
                : 'Preparing your export'}
            </DialogTitle>
            <DialogDescription>
              {exportState === 'error'
                ? 'Something went wrong while building the CSV. Please try again.'
                : 'Crunching every user row — the download starts automatically.'}
            </DialogDescription>
          </DialogHeader>
          {exportState === 'preparing' && (
            <Stack direction="horizontal" gap={2} align="center">
              <LoadingIcon size="sm" />
              <Text size="sm" color="tertiary">
                This usually takes a few seconds
              </Text>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
